import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { UserPlus, Gift, CreditCard, FileText, Megaphone } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, SectionNote, count,
} from '../../components/admin/AdminKit';

// WO-36 — signups and the one channel the app can see (referral codes). Where
// the other signups came from (a LinkedIn post, a campaign link) is not saved
// at signup yet, so it is shown as not measured instead of "all direct".

export default function AdminMarketing() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/marketing/overview');
  if (loading) return <LoadingBlock testid="admin-marketing-loading" />;
  const trend = data?.signup_trends || [];
  const max = Math.max(1, ...trend.map((d) => d.signups));

  return (
    <div className="space-y-6" data-testid="admin-marketing-page">
      <div>
        <h1 className="text-2xl font-bold">Marketing</h1>
        <p className="text-muted-foreground">Signups and where they came from (customers only)</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Signups, last 30 days" icon={UserPlus} value={count(data.total_signups_30d)} />
            <Metric label="…with a referral code" icon={Gift} value={count(data.referral_signups_30d)} />
            <Metric label="New paid subscriptions, 30 days" icon={CreditCard} value={count(data.new_paid_subscriptions_30d)}
              note="First payments at checkout" />
            <Metric label="Reports page opened, 30 days" icon={FileText} value={count(data.report_page_views_30d)} />
          </div>

          <Card className="border-0 bg-card/50">
            <CardHeader>
              <CardTitle>Signups per day</CardTitle>
              <CardDescription>Last 30 days (UTC days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-32" data-testid="signup-trend">
                {trend.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full min-w-0"
                       title={`${d.date}: ${d.signups}`}>
                    <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(d.signups / max) * 100}%`, minHeight: d.signups ? 4 : 1 }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>{trend[0]?.date}</span><span>{trend[trend.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Metric label="Where the other signups came from" icon={Megaphone} value={null}
              why="The visit's source (LinkedIn, campaign link) is not saved at signup yet — Phase 2" />
            <Metric label="Campaigns" icon={Megaphone} value={null} why="No campaign data is collected" />
          </div>
          <SectionNote>The campaign runs on LinkedIn only; saving each signup's source is part of Phase 2.</SectionNote>
        </>
      )}
    </div>
  );
}
